'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

function getStorageId(key: string, storage: Storage) {
  const existing = storage.getItem(key);
  if (existing) return existing;

  const id = `${key}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  storage.setItem(key, id);
  return id;
}

function getDeviceType() {
  const width = window.innerWidth;

  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';

  return 'desktop';
}

function getUtms(searchParams: URLSearchParams) {
  return {
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
  };
}

function classifyClick(element: HTMLElement) {
  const text = (element.innerText || element.getAttribute('aria-label') || '').trim().toLowerCase();
  const href =
    element instanceof HTMLAnchorElement
      ? element.href.toLowerCase()
      : (element.getAttribute('href') || '').toLowerCase();

  if (href.startsWith('tel:') || text.includes('bel')) return 'phone_click';

  if (
    href.includes('wa.me') ||
    href.includes('whatsapp') ||
    text.includes('whatsapp') ||
    text.includes('stuur een bericht')
  ) {
    return 'whatsapp_click';
  }

  if (href.includes('/offerte') || text.includes('offerte')) return 'quote_click';

  if (text.includes('afspraak')) return 'appointment_click';

  if (element instanceof HTMLButtonElement || element.getAttribute('role') === 'button') {
    return 'button_click';
  }

  return 'link_click';
}

async function sendEvent(payload: Record<string, unknown>) {
  try {
    await fetch('/api/track-activity', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      body: JSON.stringify(payload),
    });
  } catch {
    // Do not break the website if tracking fails.
  }
}

export default function TrackingProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedUrl = useRef('');

  useEffect(() => {
    const currentUrl = window.location.href;

    if (lastTrackedUrl.current === currentUrl) return;
    lastTrackedUrl.current = currentUrl;

    const visitorId = getStorageId('rfc_visitor_id', window.localStorage);
    const sessionId = getStorageId('rfc_session_id', window.sessionStorage);

    sendEvent({
      event_type: 'page_view',
      page_url: currentUrl,
      page_path: pathname,
      referrer: document.referrer || '',
      visitor_id: visitorId,
      session_id: sessionId,
      device_type: getDeviceType(),
      ...getUtms(searchParams),
      metadata: {
        title: document.title,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        language: navigator.language,
      },
    });
  }, [pathname, searchParams]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest('a, button, [role="button"]') as HTMLElement | null;
      if (!clickable) return;

      if (clickable.getAttribute('data-track-manual') === 'true') return;

      const visitorId = getStorageId('rfc_visitor_id', window.localStorage);
      const sessionId = getStorageId('rfc_session_id', window.sessionStorage);

      const href =
        clickable instanceof HTMLAnchorElement
          ? clickable.href
          : clickable.getAttribute('href') || '';

      sendEvent({
        event_type: classifyClick(clickable),
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: document.referrer || '',
        visitor_id: visitorId,
        session_id: sessionId,
        device_type: getDeviceType(),
        ...getUtms(new URLSearchParams(window.location.search)),
        metadata: {
          label: (clickable.innerText || clickable.getAttribute('aria-label') || '').trim(),
          href,
          id: clickable.id || '',
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
        },
      });
    }

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return null;
}
